// ── About: the full stack behind ops.ochrepoint.ca (#64) ────────────────────
// Every layer of tech and code this platform uses to exist, top to bottom.
// Hand-maintained; update when a layer is added or retired.

interface Layer {
  title: string;
  icon: string;
  items: { name: string; detail: string }[];
}

const LAYERS: Layer[] = [
  {
    title: 'Infrastructure & hosting',
    icon: 'dns',
    items: [
      { name: 'VPS (mail.onyxprecision.ca)', detail: '1 vCPU · 4 GB RAM · Ubuntu Linux (kernel 6.8) — hosts everything below plus the mail stack and the marketing site' },
      { name: 'Apache 2.4', detail: 'TLS termination + vhosts: ops.ochrepoint.ca (this app, doc-root dist/), ochrepoint.ca (marketing), admin.ochrepoint.ca (redirect shell), mail + others; proxies /api, /uploads, /r to the backend' },
      { name: "Let's Encrypt", detail: 'HTTPS certificates for every domain, auto-renewed' },
      { name: 'pm2', detail: 'keeps the backend process ("onyxops") alive and restarts it on deploys/crashes' },
      { name: 'System cron', detail: 'nightly DB backup 03:17 (gzip, 30-day rotation, /var/backups/onyxops) + monthly restore drill that actually rebuilds a scratch database and counts tables' },
    ],
  },
  {
    title: 'Frontend (what you tap)',
    icon: 'web',
    items: [
      { name: 'React 18 + TypeScript 5 (strict)', detail: 'the entire UI — admin app, employee portal, client portal, demo sandbox — one codebase, role-gated routes' },
      { name: 'Vite 6', detail: 'build tool; `vite build` compiles to hashed bundles deployed via a side-folder swap so a failed build can never blank production' },
      { name: 'Tailwind CSS 3.4', detail: 'styling + the warm-charcoal dark mode (utility-override skin, OS-aware, per-device toggle)' },
      { name: 'React Router', detail: 'nested URLs (clients › jobs › documents…), breadcrumbs, portal drill-downs' },
      { name: 'axios', detail: 'API client with automatic JWT attach + silent token refresh on 401' },
      { name: 'PWA (manifest + service worker v2)', detail: 'installable from the home screen; offline app-shell + last-good portal data; timesheet entries queue on-device in dead zones and auto-send when signal returns' },
      { name: 'Manrope / Source Code Pro / Material Symbols', detail: 'typography and iconography' },
    ],
  },
  {
    title: 'Backend (the engine)',
    icon: 'settings_suggest',
    items: [
      { name: 'Node.js 20 + Express 4', detail: '~60 route modules on port 5000: jobs, quotes, invoices, payroll, scheduling, planner, drafting, safety, POs, mail, analytics, dev queue…' },
      { name: 'tsx runtime', detail: 'runs the TypeScript server directly (a proposal to precompile is in the queue)' },
      { name: 'JWT auth', detail: 'access + refresh tokens; roles owner / admin / pm / employee / client enforced per route' },
      { name: 'helmet · cors · multer · node-cron', detail: 'security headers, cross-origin policy (incl. the marketing-site tracking beacon), file uploads (receipts, docs, mail attachments), scheduled jobs (mail sync every 3 min, reminders, alerts)' },
      { name: 'nodemailer + mailparser', detail: 'sends branded email through local Postfix; parses the Maildir for the in-app email manager' },
    ],
  },
  {
    title: 'Data layer',
    icon: 'database',
    items: [
      { name: 'MariaDB 10.11 — construction_db', detail: '~90 tables: clients, jobs, quotes/estimates, invoices (+Stripe payments), time entries, schedule, planner tasks, expenses, documents, email, site analytics, change requests…' },
      { name: 'Migrations 001–053', detail: 'idempotent schema history; canonical schema auto-applies on server start' },
      { name: 'Backups', detail: 'nightly dumps w/ 30-day retention + monthly automated restore drill with owner notification on failure' },
      { name: 'Maildir (Postfix + Dovecot)', detail: 'real *@ochrepoint.ca mail storage — synced into MySQL for the /mail manager; sends file back into the true Sent folder' },
    ],
  },
  {
    title: 'Integrations & AI',
    icon: 'hub',
    items: [
      { name: 'Anthropic Claude', detail: 'receipt scanning + expense categorization, plan/vision takeoffs, quote follow-up drafts, daily photo progress summaries, AI email reply drafts, outreach templates' },
      { name: 'Stripe (test mode)', detail: 'client invoice payments — checkout links, CAD, auto-recorded partial/full payments, receipt emails' },
      { name: 'Open-Meteo', detail: 'keyless Cowichan weather on the planner + schedule with pour/rain risk flags' },
      { name: 'ip-api.com', detail: 'geo + VPN/proxy enrichment for marketing-site analytics bot scoring' },
      { name: 'Site tracker (self-hosted)', detail: 'ochrepoint.ca analytics — visitors, sessions, referral links (/r/slug), JS error log with resolve-to-fix-queue' },
      { name: 'SMS bridge (ADB)', detail: 'outreach texting through a tethered Android phone' },
      { name: 'puppeteer', detail: 'headless Chrome — page screenshots for the feature inventory and Claude’s deploy verification' },
    ],
  },
  {
    title: 'Development pipeline (how it evolves)',
    icon: 'engineering',
    items: [
      { name: 'Change-request queue', detail: 'this dashboard + the floating flag on every page → Claude works items autonomously (watcher every ~7 min + daily run + Start-now), verifies against production, and writes a changelog per item — 60+ requests fulfilled to date' },
      { name: 'Suggestions loop', detail: 'Claude audits the platform and proposes improvements; nothing is built until you approve it here' },
      { name: 'Safety rails', detail: 'pre-deploy dist snapshots (.snapshots/), atomic side-folder builds, typecheck-clean gate, screenshot verification, per-item rollback references' },
      { name: 'Demo sandbox', detail: '?demo=role in-browser fake-data mode with 248 API handlers — the portal previews behind this dash' },
      { name: 'devlog/', detail: 'session-by-session status history, runbook, decisions, known issues' },
    ],
  },
];

export function AboutTechTab() {
  return (
    <div className="max-w-3xl space-y-4">
      <div className="bg-[#FAF6F0] border border-[#C77D3F]/25 rounded-xl p-4">
        <h2 className="font-bold text-gray-900 flex items-center gap-2">
          <span className="material-symbols-outlined text-[#C77D3F]">layers</span>
          What ops.ochrepoint.ca is made of
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Every layer between a tap on your phone and the metal: one rented server, one database,
          one React app, and an AI-driven build pipeline. Rundown current as of July 2026.
        </p>
      </div>

      {LAYERS.map(layer => (
        <div key={layer.title} className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-[#C77D3F] text-xl">{layer.icon}</span>
            {layer.title}
          </h3>
          <div className="divide-y divide-gray-50">
            {layer.items.map(it => (
              <div key={it.name} className="py-2 grid sm:grid-cols-[220px_1fr] gap-x-4 gap-y-0.5">
                <span className="text-sm font-semibold text-gray-800">{it.name}</span>
                <span className="text-sm text-gray-500">{it.detail}</span>
              </div>
            ))}
          </div>
        </div>
      ))}

      <p className="text-[11px] text-gray-400 px-1">
        Request flow: your tap → Apache (TLS) → React app in the browser → axios /api call →
        Express route → MariaDB → back up the chain — with pm2, snapshots, and nightly backups underneath.
      </p>
    </div>
  );
}
